import React, { useState, useEffect } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { fetchPropertyData } from '../services/propertyService';
import { useAuth } from '../context/AuthContext';
import { saveComparison, getUserComparisons, deleteComparison, updateComparison } from '../services/comparisonService';
import { getPlaceSuggestions } from '../services/awsConfig';
import '../styles/PropertyComparison.css';

const PropertyComparison = ({ currentProperty }) => {
  const { subscription, canPerformAction, trackPropertyAnalysis } = useSubscription();
  const { user } = useAuth();
  const isPremium = subscription?.tier === 'PREMIUM' || subscription?.tier === 'UNLIMITED';
  
  const [comparisonProperties, setComparisonProperties] = useState([]);
  const [newPropertyAddress, setNewPropertyAddress] = useState('');
  const [newPropertyUnitNumber, setNewPropertyUnitNumber] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [comparisonName, setComparisonName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingComparisons, setLoadingComparisons] = useState(false);
  const [editingComparison, setEditingComparison] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editComparisonName, setEditComparisonName] = useState('');

  // Load saved comparisons when component mounts
  useEffect(() => {
    if (user?.userId) {
      loadSavedComparisons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const loadSavedComparisons = async () => {
    try {
      setLoadingComparisons(true);
      const comparisons = await getUserComparisons(user?.userId);
      setSavedComparisons(comparisons);
    } catch (error) {
      console.error('Failed to load saved comparisons:', error);
    } finally {
      setLoadingComparisons(false);
    }
  };

  const handleAddressChange = async (e) => {
    const query = e.target.value;
    setNewPropertyAddress(query);

    if (query.length > 2) {
      setIsLoadingSuggestions(true);
      try {
        const results = await getPlaceSuggestions(query);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setError('Error fetching address suggestions. Please try again.');
      } finally {
        setIsLoadingSuggestions(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setNewPropertyAddress(suggestion);
    setSuggestions([]);
  };

  const addPropertyForComparison = async () => {
    if (!newPropertyAddress.trim()) return;
    
    if (!canPerformAction('property_analysis')) {
      setError('Monthly limit reached. Upgrade your plan to analyze more properties.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Combine address and unit number like on home page
      const fullAddress = newPropertyUnitNumber.trim() 
        ? `${newPropertyAddress.trim()}, Unit ${newPropertyUnitNumber.trim()}`
        : newPropertyAddress.trim();
      
      const propertyData = await fetchPropertyData(fullAddress, user?.userId);
      
      const comparisonData = {
        address: fullAddress,
        ...propertyData,
        id: Date.now()
      };
      
      setComparisonProperties(prev => [...prev, comparisonData]);
      setNewPropertyAddress('');
      setNewPropertyUnitNumber('');
      setSuggestions([]);
      
      if (user) {
        trackPropertyAnalysis();
      }
    } catch (err) {
      setError(`Failed to load property data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveComparison = async () => {
    if (!comparisonName.trim()) {
      setError('Please enter a name for your comparison');
      return;
    }

    if (comparisonProperties.length === 0) {
      setError('Add at least one property to save a comparison');
      return;
    }

    try {
      setSaving(true);
      await saveComparison(user?.userId, comparisonName, comparisonProperties);
      setShowSaveModal(false);
      setComparisonName('');
      loadSavedComparisons(); // Refresh the list
      setError('');
      alert('Comparison saved successfully!');
    } catch (error) {
      setError(`Failed to save comparison: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const loadComparison = (comparison) => {
    setComparisonProperties(comparison.properties);
    setError('');
  };

  const handleDeleteComparison = async (comparisonId) => {
    if (!window.confirm('Are you sure you want to delete this comparison?')) {
      return;
    }

    try {
      await deleteComparison(user?.userId, comparisonId);
      loadSavedComparisons(); // Refresh the list
    } catch (error) {
      setError(`Failed to delete comparison: ${error.message}`);
    }
  };

  const handleEditComparison = (comparison) => {
    setEditingComparison(comparison);
    setEditComparisonName(comparison.comparisonName);
    setShowEditModal(true);
    setError('');
  };

  const handleUpdateComparison = async () => {
    if (!editComparisonName.trim()) {
      setError('Please enter a name for your comparison');
      return;
    }

    if (!editingComparison) {
      setError('No comparison selected for editing');
      return;
    }

    try {
      setSaving(true);
      await updateComparison(user?.userId, editingComparison.comparisonId, {
        comparisonName: editComparisonName,
        properties: comparisonProperties.length > 0 ? comparisonProperties : editingComparison.properties
      });
      setShowEditModal(false);
      setEditingComparison(null);
      setEditComparisonName('');
      loadSavedComparisons(); // Refresh the list
      setError('');
      alert('Comparison updated successfully!');
    } catch (error) {
      setError(`Failed to update comparison: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const removeProperty = (id) => {
    setComparisonProperties(prev => prev.filter(p => p.id !== id));
  };

  const calculateMetrics = (property) => {
    const price = parseFloat(property.propertyPrice) || 0;
    const monthlyRent = parseFloat(property.monthlyRent) || 0;
    const annualRent = monthlyRent * 12;
    const vacancyRate = parseFloat(property.vacancyRate) || 5; // Default 5% if not provided
    const managementFeePercentage = parseFloat(property.managementFeePercentage) || 10; // Default 10% if not provided
    const annualAdditionalExpenses = (parseFloat(property.additionalExpenses) || 0) * 12;
    
    // Calculate vacancy loss (matching FormPage logic)
    const vacancyLoss = annualRent * (vacancyRate / 100);
    const managementFee = annualRent * (managementFeePercentage / 100);
    const annualHOA = (parseFloat(property.monthlyHOA) || 0) * 12;
    const yearlyTax = parseFloat(property.propertyTax) || 0;
    const yearlyInsurance = parseFloat(property.yearlyInsurance) || 0;
    const yearlyMaintenance = parseFloat(property.yearlyMaintenance) || 0;
    
    // Calculate total expenses (matching FormPage logic)
    const totalExpenses = vacancyLoss + managementFee + annualHOA + yearlyTax + yearlyInsurance + yearlyMaintenance + annualAdditionalExpenses;
    const netOperatingIncome = annualRent - totalExpenses;
    
    // Cap Rate - monthly mortgage is not included in capRate (matching FormPage logic)
    const capRate = price > 0 ? (((netOperatingIncome + annualAdditionalExpenses) / price) * 100) : 0;
    
    return {
      capRate: capRate.toFixed(2), // Match FormPage precision
      rentToPrice: price > 0 ? ((monthlyRent / price) * 100).toFixed(3) : '0',
      grossRentMultiplier: annualRent > 0 ? (price / annualRent).toFixed(2) : '0',
      cashFlow: netOperatingIncome.toFixed(0)
    };
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    return num < 0 ? `-$${Math.abs(num).toLocaleString()}` : `$${num.toLocaleString()}`;
  };

  const getBestProperty = (metric) => {
    if (comparisonProperties.length === 0) return null;
    
    return comparisonProperties.reduce((best, current) => {
      const currentMetrics = calculateMetrics(current);
      const bestMetrics = calculateMetrics(best);
      
      switch (metric) {
        case 'capRate':
          return parseFloat(currentMetrics.capRate) > parseFloat(bestMetrics.capRate) ? current : best;
        case 'cashFlow':
          return parseFloat(currentMetrics.cashFlow) > parseFloat(bestMetrics.cashFlow) ? current : best;
        case 'price':
          return parseFloat(current.propertyPrice) < parseFloat(best.propertyPrice) ? current : best;
        default:
          return best;
      }
    });
  };

  return (
    <div className="property-comparison">
      <div className="comparison-header">
        <h3>Property Comparison</h3>
        <p>Compare up to 5 properties side-by-side. Each new property added to the comparison counts toward your monthly property search limit.</p>
      </div>

      {/* Saved Comparisons Section */}
      {savedComparisons.length > 0 && (
        <div className="saved-comparisons-section">
          <h4>Saved Comparisons</h4>
          {loadingComparisons ? (
            <div className="loading-text">Loading saved comparisons...</div>
          ) : (
            <div className="saved-comparisons-list">
              {savedComparisons.map((comparison) => (
                <div key={comparison.comparisonId} className="saved-comparison-item">
                  <div className="comparison-info">
                    <span className="comparison-name">{comparison.comparisonName}</span>
                    <span className="comparison-count">{comparison.properties.length} properties</span>
                    <span className="comparison-date">
                      {new Date(comparison.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="comparison-actions">
                    <button
                      onClick={() => loadComparison(comparison)}
                      className="load-btn"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleEditComparison(comparison)}
                      className="rename-btn"
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => handleDeleteComparison(comparison.comparisonId)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="add-property-section">
        <div className="add-property-form">
          <div className="autocomplete-container">
            <input
              type="text"
              value={newPropertyAddress}
              onChange={handleAddressChange}
              placeholder="Start typing property address..."
              className="property-input address-input"
              disabled={loading || comparisonProperties.length >= 4}
            />
            {isLoadingSuggestions && (
              <div className="autocomplete-loading">🔍 Searching...</div>
            )}
            {suggestions.length > 0 && (
              <div className="autocomplete-suggestions">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            value={newPropertyUnitNumber}
            onChange={(e) => setNewPropertyUnitNumber(e.target.value)}
            placeholder="Unit # (optional)"
            className="property-input unit-input"
            disabled={loading || comparisonProperties.length >= 4}
          />
          <button
            onClick={addPropertyForComparison}
            disabled={loading || !newPropertyAddress.trim() || comparisonProperties.length >= 4}
            className="add-btn"
          >
            {loading ? 'Loading...' : 'Add Property'}
          </button>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="comparison-limit">
          {comparisonProperties.length}/4 properties added
        </div>
        {comparisonProperties.length > 0 && (
          <div className="comparison-actions-section">
            <button
              onClick={() => setShowSaveModal(true)}
              className="save-comparison-btn"
            >
              Save Comparison
            </button>
          </div>
        )}
      </div>

      {comparisonProperties.length > 0 && (
        <div className="comparison-table-container">
          <div className="comparison-table">
            <div className="table-header">
              <div className="metric-column">Metric</div>
              {currentProperty && (
                <div className="property-column current-property">
                  <span className="property-address">Current Property</span>
                  <span className="property-badge">Current</span>
                </div>
              )}
              {comparisonProperties.map((property) => (
                <div key={property.id} className="property-column">
                  <span className="property-address">{property.address}</span>
                  <button 
                    onClick={() => removeProperty(property.id)}
                    className="remove-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="table-body">
              {/* Price Row */}
              <div className="table-row">
                <div className="metric-name">Purchase Price</div>
                {currentProperty && (
                  <div className="metric-value">
                    {formatCurrency(currentProperty.propertyPrice)}
                  </div>
                )}
                {comparisonProperties.map((property) => {
                  const metrics = calculateMetrics(property);
                  const isBest = getBestProperty('price')?.id === property.id;
                  return (
                    <div key={property.id} className={`metric-value ${isBest ? 'best-value' : ''}`}>
                      {formatCurrency(property.propertyPrice)}
                      {isBest && <span className="best-indicator">👑</span>}
                    </div>
                  );
                })}
              </div>

              {/* Monthly Rent Row */}
              <div className="table-row">
                <div className="metric-name">Monthly Rent</div>
                {currentProperty && (
                  <div className="metric-value">
                    {formatCurrency(currentProperty.monthlyRent)}
                  </div>
                )}
                {comparisonProperties.map((property) => (
                  <div key={property.id} className="metric-value">
                    {formatCurrency(property.monthlyRent)}
                  </div>
                ))}
              </div>

              {/* Cap Rate Row */}
              <div className="table-row">
                <div className="metric-name">Cap Rate</div>
                {currentProperty && (
                  <div className="metric-value">
                    {calculateMetrics(currentProperty).capRate}%
                  </div>
                )}
                {comparisonProperties.map((property) => {
                  const metrics = calculateMetrics(property);
                  const isBest = getBestProperty('capRate')?.id === property.id;
                  return (
                    <div key={property.id} className={`metric-value ${isBest ? 'best-value' : ''}`}>
                      {metrics.capRate}%
                      {isBest && <span className="best-indicator">👑</span>}
                    </div>
                  );
                })}
              </div>

              {/* Cash Flow Row */}
              <div className="table-row">
                <div className="metric-name">Annual Cash Flow</div>
                {currentProperty && (
                  <div className="metric-value">
                    {formatCurrency(calculateMetrics(currentProperty).cashFlow)}
                  </div>
                )}
                {comparisonProperties.map((property) => {
                  const metrics = calculateMetrics(property);
                  const isBest = getBestProperty('cashFlow')?.id === property.id;
                  const cashFlow = parseFloat(metrics.cashFlow);
                  return (
                    <div key={property.id} className={`metric-value ${isBest ? 'best-value' : ''} ${cashFlow > 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(metrics.cashFlow)}
                      {isBest && <span className="best-indicator">👑</span>}
                    </div>
                  );
                })}
              </div>

              {/* Rent-to-Price Ratio Row */}
              <div className="table-row">
                <div className="metric-name">Rent-to-Price Ratio</div>
                {currentProperty && (
                  <div className="metric-value">
                    {calculateMetrics(currentProperty).rentToPrice}%
                  </div>
                )}
                {comparisonProperties.map((property) => {
                  const metrics = calculateMetrics(property);
                  return (
                    <div key={property.id} className="metric-value">
                      {metrics.rentToPrice}%
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="comparison-summary">
            <h4>Quick Analysis</h4>
            <div className="summary-insights">
              {getBestProperty('capRate') && (
                <div className="insight">
                  <span className="insight-icon">🎯</span>
                  <span>Best Cap Rate: {getBestProperty('capRate').address}</span>
                </div>
              )}
              {getBestProperty('cashFlow') && (
                <div className="insight">
                  <span className="insight-icon">💰</span>
                  <span>Best Cash Flow: {getBestProperty('cashFlow').address}</span>
                </div>
              )}
              {getBestProperty('price') && (
                <div className="insight">
                  <span className="insight-icon">💵</span>
                  <span>Lowest Price: {getBestProperty('price').address}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {comparisonProperties.length === 0 && (
        <div className="no-comparisons">
          <div className="no-comparisons-icon">📊</div>
          <p>No properties added for comparison yet.</p>
          <p>Add properties above to see detailed side-by-side analysis.</p>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Save Comparison</h3>
            <div className="modal-body">
              <input
                type="text"
                value={comparisonName}
                onChange={(e) => setComparisonName(e.target.value)}
                placeholder="Enter comparison name..."
                className="comparison-name-input"
                autoFocus
              />
              <p className="modal-description">
                Save this comparison with {comparisonProperties.length} properties to access it later.
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowSaveModal(false)}
                className="cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveComparison}
                className="save-btn"
                disabled={saving || !comparisonName.trim()}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Rename Comparison</h3>
            <div className="modal-body">
              <input
                type="text"
                value={editComparisonName}
                onChange={(e) => setEditComparisonName(e.target.value)}
                placeholder="Enter comparison name..."
                className="comparison-name-input"
                autoFocus
              />
              <p className="modal-description">
                {comparisonProperties.length > 0 
                  ? `Update this comparison with current ${comparisonProperties.length} properties and new name.`
                  : `Update the name for this comparison with ${editingComparison?.properties?.length || 0} properties.`
                }
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingComparison(null);
                  setEditComparisonName('');
                }}
                className="cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateComparison}
                className="save-btn"
                disabled={saving || !editComparisonName.trim()}
              >
                {saving ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyComparison;